import { ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import { DialogContext } from './DialogContext';
import styles from './DialogPage.module.scss';
import { Heading } from '../text/Heading';

interface Props {
  children: (args: {
    close: () => void;
    push: (id: string) => void;
    pop: () => void;
  }) => ReactNode;
  id: string;
  heading: string;
}

export function DialogPage(props: Props) {
  const {
    close,
    currentPage,
    nextPages,
    pages,
    prevPages,
    rootPage,
    setCurrentPage,
    setNextPages,
    setPrevPages,
  } = useContext(DialogContext);
  const active = currentPage === props.id;
  const ref = useRef<HTMLDivElement | null>(null);

  // Register page on initial load
  useEffect(() => {
    pages.add(props.id); // Non state var (prevents re-renders)
    // root page is excluded
    if (props.id !== rootPage) {
      setNextPages((next) =>
        next.has(props.id) ? next : new Set(next).add(props.id),
      );
    }
  }, [pages, props.id, rootPage, setNextPages]);

  const push = useCallback(
    (id: string) => {
      setCurrentPage(id);
      setPrevPages((state) => [...state, currentPage]);
      setNextPages((state) => {
        const nextState = new Set(state);
        nextState.delete(id);
        return nextState;
      });
    },
    [currentPage, setCurrentPage, setNextPages, setPrevPages],
  );

  const pop = useCallback(() => {
    const id = prevPages.pop();
    if (id) {
      setNextPages((state) => {
        return new Set(state).add(props.id);
      });
      setPrevPages(prevPages);
      setCurrentPage(id);
    }
  }, [prevPages, props.id, setNextPages, setPrevPages, setCurrentPage]);

  const dialogCassname = [
    styles['dialog-page'],
    active && styles['dialog-page-active'],
    prevPages.includes(props.id) && styles['dialog-page-prev'],
    nextPages.has(props.id) && styles['dialog-page-next'],
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      aria-hidden={!active}
      className={dialogCassname}
      inert={!active}
      tabIndex={-1}
    >
      <Heading level={2} spacing="bottom">
        {props.heading}
      </Heading>
      {props.children({ close, pop, push })}
    </div>
  );
}
